import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { hasPortalAccess, isOperatorRole, sessionRevoked } from '../../redux/slices/authSlice';

const REVOKED_MESSAGE = 'Ihr Portalzugang wurde deaktiviert. Bitte wenden Sie sich an die Zentrale.';
const OPERATOR_INACTIVE_MESSAGE =
  'Das Unternehmen dieses Zugangs wurde deaktiviert. Bitte wenden Sie sich an die Zentrale.';

/**
 * Beendet die Sitzung, sobald der Zugang während der Nutzung entzogen wird.
 *
 * Bis hierher wurde der Zugriff nur beim Login und beim Laden der Anwendung
 * geprüft. Deaktivierte ein Admin einen Zugang, lief dessen offener Tab weiter —
 * die Firestore-Rules verweigerten zwar sofort jeden Zugriff, aber sichtbar war
 * das nur als stiller Fehlschlag statt als Abmeldung.
 *
 * Gehört bewusst nicht in `checkAuthState`: Der Listener muss über die ganze
 * Sitzung laufen, nicht nur bei deren Beginn.
 */
const PortalAccessWatcher = () => {
  const dispatch = useDispatch();
  const uid = useSelector(state => state.auth.user?.uid);

  useEffect(() => {
    if (!uid) return undefined;

    const endSession = async (message) => {
      try {
        await signOut(auth);
      } catch (error) {
        console.warn('Abmeldung nach Zugangsentzug fehlgeschlagen:', error);
      }
      dispatch(sessionRevoked(message));
    };

    const unsubscribe = onSnapshot(
      doc(db, 'users', uid),
      async (snapshot) => {
        // Während einer regulären Abmeldung ist hier nichts mehr zu prüfen.
        if (!auth.currentUser) return;

        // Gelöschtes Konto: der Zugang existiert nicht mehr.
        if (!snapshot.exists()) {
          await endSession(REVOKED_MESSAGE);
          return;
        }

        const userData = snapshot.data();
        if (!hasPortalAccess(userData)) {
          await endSession(REVOKED_MESSAGE);
          return;
        }

        // Ein deaktiviertes Unternehmen sperrt auch seine Zugänge. Das eigene
        // operators-Dokument darf der Zugang nach den Rules lesen.
        if (isOperatorRole(userData) && userData.operatorId) {
          try {
            const operatorDoc = await getDoc(doc(db, 'operators', userData.operatorId));
            if (!operatorDoc.exists() || operatorDoc.data().active === false) {
              await endSession(OPERATOR_INACTIVE_MESSAGE);
            }
          } catch (error) {
            // Leserechte weg = Zugang weg; das ist genau der Rules-Zustand für
            // ein deaktiviertes Unternehmen.
            console.warn('Unternehmensstatus nicht lesbar:', error);
            await endSession(OPERATOR_INACTIVE_MESSAGE);
          }
        }
      },
      (error) => {
        // Ein PERMISSION_DENIED auf dem eigenen Dokument heißt: das Konto wurde
        // gesperrt oder entfernt. Nur solange die Anmeldung überhaupt noch
        // besteht — bei einer regulären Abmeldung schlägt der Listener ebenfalls
        // fehl, und dann wäre die Meldung schlicht falsch. Andere Fehler (Netz)
        // bleiben folgenlos, die nächste Verbindung liefert wieder Daten.
        if (error?.code === 'permission-denied' && auth.currentUser) {
          endSession(REVOKED_MESSAGE);
          return;
        }
        console.warn('Zugangsüberwachung unterbrochen:', error);
      }
    );

    return unsubscribe;
  }, [uid, dispatch]);

  return null;
};

export default PortalAccessWatcher;
